import React from 'react';
import { stateOptions } from '../commonStateTaxData';

export const step1Questions = [
  {
    id: 'employee_first_name',
    title: 'First name',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'employee_middle_name',
    title: 'Middle initial',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[a-zA-Z]{1}$',
        customErrorMessage: 'The middle initial can only contain 1 letter.',
      },
    ],
  },
  {
    id: 'employee_last_name',
    title: 'Last name',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'employee_ssn',
    title: 'Social security number',
    is_required: true,
    placeholder: '9 digits',
    question_type: 'identity',
    identity_type: 'ssn',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^\\d{9}$',
      },
    ],
  },
  {
    id: 'street_address',
    title: 'Home address',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'city_or_town',
    title: 'City or town',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'state',
    title: 'State',
    is_required: true,
    question_type: 'dropdown',
    options: stateOptions,
  },
  {
    id: 'zip_code',
    title: 'Zip code',
    is_required: true,
    question_type: 'short_text',
    placeholder: '5 digits',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^\\d{5}$',
      },
    ],
  },
  {
    id: 'filing_status',
    title: 'Please select filing status:',
    is_required: true,
    question_type: 'multiple_choice',
    options: [
      {
        codeValue: 'single_or_married_filing_separately',
        shortName: 'Single or married filing separately',
      },
      {
        codeValue: 'head_of_household',
        shortName: 'Head of household',
      },
      {
        codeValue: 'married_filing_jointly_or_surviving_spouse',
        shortName: 'Married filing jointly or surviving spouse',
      },
    ],
  },
];

export const step1QuestionsWithCounty = [
  ...step1Questions.slice(0, -1),
  // Slot this question before the filing status question
  {
    id: 'county',
    title: 'County (Enter first five letters)',
    question_type: 'short_text',
    is_required: false,
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[a-zA-Z]{1,5}$',
        customErrorMessage: 'Enter up to the first five letters of the county.',
      },
    ],
  },
  ...step1Questions.slice(-1),
];

export const allowanceAndAdditionalAmt = [
  {
    id: 'total_num_allowances',
    title: (
      <span>
        Total number of allowances you are claiming
        <br />
        (Enter zero (0), or the number of allowances from
        {' '}
        <a href="https://check-api-static-production.s3.amazonaws.com/forms/pdf/nc_nc4_05.pdf" target="_blank" rel="noopener noreferrer">
          Page 2, Line 17 of the NC-4 Allowance Worksheet
        </a>
        )
      </span>
    ),
    question_type: 'short_text',
    is_required: true,
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^(0|[1-9][0-9]*)$',
      },
    ],
  },
  {
    id: 'additional_amount',
    title: 'Additional amount, if any, you want withheld from each pay period (Enter whole dollars)',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[0-9]+$',
        customErrorMessage: 'Enter a valid whole dollars',
      },
    ],
  },
];

export const worksheetInitialQuestions = [
  {
    id: 'worksheet_filing_status',
    title: 'Select your filing status:',
    question_type: 'dropdown',
    is_required: true,
    options: [
      {
        codeValue: 'single',
        shortName: 'Single',
      },
      {
        codeValue: 'married_filing_jointly',
        shortName: 'Married filing jointly',
      },
      {
        codeValue: 'married_filing_separately',
        shortName: 'Married filing separately',
      },
      {
        codeValue: 'head_of_household',
        shortName: 'Head of household',
      },
      {
        codeValue: 'surviving_spouse',
        shortName: 'Surviving spouse',
      },
    ],
  },
];

export const maritalStatusOptions920 = {
  single: [
    {
      id: 'itemized_deductions_exceed_13249',
      title: 'Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $13,249?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'child_deduction_amount_exceed_2499',
      title: 'Will your N.C. Child Deduction Amount from Page 3, Schedule 2 exceed $2,499?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'federal_adjustments_or_state_deductions',
      title: 'Will you have federal adjustments or State deductions from income?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'claim_tax_credits_or_carryovers',
      title: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'form_part_2_line_17',
      title: 'You may choose to use to determine if you qualify for additional allowances. Enter the value from line 17 of the worksheet, or you may also enter 0 allowances.',
      question_type: 'short_text',
      is_required: true,
      rules: [
        {
          category: 'regex',
          action: 'match',
          value: '^(0|[1-9][0-9]*)$',
        },
      ],
    },
  ],
  married_filing_jointly: [
    {
      id: 'itemized_deductions_exceed_23999',
      title: 'Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $23,999?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'child_deduction_amount_exceed_2499',
      title: 'Will your N.C. Child Deduction Amount from Page 3, Schedule 2 exceed $2,499?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'federal_adjustments_or_state_deductions',
      title: 'Will you have federal adjustments or State deductions from income?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'claim_tax_credits_or_carryovers',
      title: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'spouse_receive_combined_wages_and_taxable_retirement_benefits',
      title: 'Will your spouse receive combined wages and taxable retirement benefits of less than $8,250 or only retirement benefits not subject to N.C. income tax?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'form_part_2_line_17',
      title: 'You may choose to use to determine if you qualify for additional allowances. Enter the value from line 17 of the worksheet, or you may also enter 0 allowances.',
      question_type: 'short_text',
      is_required: true,
      rules: [
        {
          category: 'regex',
          action: 'match',
          value: '^(0|[1-9][0-9]*)$',
        },
      ],
    },
  ],
  married_filing_separately: [
    {
      id: 'itemized_deductions_exceed_13249',
      title: 'Will your portion of N.C. itemized deductions from Page 3, Schedule 1 exceed $13,249?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'child_deduction_amount_exceed_2499',
      title: 'Will your N.C. Child Deduction Amount from Page 3, Schedule 2 exceed $2,499?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'federal_adjustments_or_state_deductions',
      title: 'Will you have federal adjustments or State deductions from income?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'claim_tax_credits_or_carryovers',
      title: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'form_part_2_line_17',
      title: 'You may choose to use to determine if you qualify for additional allowances. Enter the value from line 17 of the worksheet, or you may also enter 0 allowances.',
      question_type: 'short_text',
      is_required: true,
      rules: [
        {
          category: 'regex',
          action: 'match',
          value: '^(0|[1-9][0-9]*)$',
        },
      ],
    },
  ],
  head_of_household: [
    {
      id: 'itemized_deductions_exceed_18624',
      title: 'Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $18,624?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'child_deduction_amount_exceed_2499',
      title: 'Will your N.C. Child Deduction Amount from Page 3, Schedule 2 exceed $2,499?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'federal_adjustments_or_state_deductions',
      title: 'Will you have federal adjustments or State deductions from income?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'claim_tax_credits_or_carryovers',
      title: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'form_part_2_line_17',
      title: 'You may choose to use to determine if you qualify for additional allowances. Enter the value from line 17 of the worksheet, or you may also enter 0 allowances.',
      question_type: 'short_text',
      is_required: true,
      rules: [
        {
          category: 'regex',
          action: 'match',
          value: '^(0|[1-9][0-9]*)$',
        },
      ],
    },
  ],
  surviving_spouse: [
    {
      id: 'itemized_deductions_exceed_23999',
      title: 'Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $23,999?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'child_deduction_amount_exceed_2499',
      title: 'Will your N.C. Child Deduction Amount from Page 3, Schedule 2 exceed $2,499?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'federal_adjustments_or_state_deductions',
      title: 'Will you have federal adjustments or State deductions from income?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'claim_tax_credits_or_carryovers',
      title: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'form_part_2_line_17',
      title: 'You may choose to use to determine if you qualify for additional allowances. Enter the value from line 17 of the worksheet, or you may also enter 0 allowances.',
      question_type: 'short_text',
      is_required: true,
      rules: [
        {
          category: 'regex',
          action: 'match',
          value: '^(0|[1-9][0-9]*)$',
        },
      ],
    },
  ],
};

export const maritalStatusOptions1121 = {
  single: [
    {
      id: 'itemized_deductions_exceed_13249',
      title: 'Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $15,249?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'child_deduction_amount_exceed_2499',
      title: 'Will your N.C. Child Deduction Amount from Page 3, Schedule 2 exceed $2,499?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'federal_adjustments_or_state_deductions',
      title: 'Will you have federal adjustments or State deductions from income?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'claim_tax_credits_or_carryovers',
      title: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'form_part_2_line_17',
      title: 'You may choose to use to determine if you qualify for additional allowances. Enter the value from line 17 of the worksheet, or you may also enter 0 allowances.',
      question_type: 'short_text',
      is_required: true,
      rules: [
        {
          category: 'regex',
          action: 'match',
          value: '^(0|[1-9][0-9]*)$',
        },
      ],
    },
  ],
  married_filing_jointly: [
    {
      id: 'itemized_deductions_exceed_23999',
      title: 'Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $27,999?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'child_deduction_amount_exceed_2499',
      title: 'Will your N.C. Child Deduction Amount from Page 3, Schedule 2 exceed $2,499?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'federal_adjustments_or_state_deductions',
      title: 'Will you have federal adjustments or State deductions from income?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'claim_tax_credits_or_carryovers',
      title: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'spouse_receive_combined_wages_and_taxable_retirement_benefits',
      title: 'Will your spouse receive combined wages and taxable retirement benefits of less than $10,250 or only retirement benefits not subject to N.C. income tax?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'form_part_2_line_17',
      title: 'You may choose to use to determine if you qualify for additional allowances. Enter the value from line 17 of the worksheet, or you may also enter 0 allowances.',
      question_type: 'short_text',
      is_required: true,
      rules: [
        {
          category: 'regex',
          action: 'match',
          value: '^(0|[1-9][0-9]*)$',
        },
      ],
    },
  ],
  married_filing_separately: [
    {
      id: 'itemized_deductions_exceed_13249',
      title: 'Will your portion of N.C. itemized deductions from Page 3, Schedule 1 exceed $15,249?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'child_deduction_amount_exceed_2499',
      title: 'Will your N.C. Child Deduction Amount from Page 3, Schedule 2 exceed $2,499?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'federal_adjustments_or_state_deductions',
      title: 'Will you have federal adjustments or State deductions from income?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'claim_tax_credits_or_carryovers',
      title: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'form_part_2_line_17',
      title: 'You may choose to use to determine if you qualify for additional allowances. Enter the value from line 17 of the worksheet, or you may also enter 0 allowances.',
      question_type: 'short_text',
      is_required: true,
      rules: [
        {
          category: 'regex',
          action: 'match',
          value: '^(0|[1-9][0-9]*)$',
        },
      ],
    },
  ],
  head_of_household: [
    {
      id: 'itemized_deductions_exceed_18624',
      title: 'Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $21,624?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'child_deduction_amount_exceed_2499',
      title: 'Will your N.C. Child Deduction Amount from Page 3, Schedule 2 exceed $2,499?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'federal_adjustments_or_state_deductions',
      title: 'Will you have federal adjustments or State deductions from income?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'claim_tax_credits_or_carryovers',
      title: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'form_part_2_line_17',
      title: 'You may choose to use to determine if you qualify for additional allowances. Enter the value from line 17 of the worksheet, or you may also enter 0 allowances.',
      question_type: 'short_text',
      is_required: true,
      rules: [
        {
          category: 'regex',
          action: 'match',
          value: '^(0|[1-9][0-9]*)$',
        },
      ],
    },
  ],
  surviving_spouse: [
    {
      id: 'itemized_deductions_exceed_23999',
      title: 'Will your N.C. itemized deductions from Page 3, Schedule 1 exceed $27,999?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'child_deduction_amount_exceed_2499',
      title: 'Will your N.C. Child Deduction Amount from Page 3, Schedule 2 exceed $2,499?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'federal_adjustments_or_state_deductions',
      title: 'Will you have federal adjustments or State deductions from income?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'claim_tax_credits_or_carryovers',
      title: 'Will you be able to claim any N.C. tax credits or tax credit carryovers?',
      question_type: 'multiple_choice',
      is_required: true,
      options: [
        {
          codeValue: 'true',
          shortName: 'Yes',
        },
        {
          codeValue: 'false',
          shortName: 'No',
        },
      ],
    },
    {
      id: 'form_part_2_line_17',
      title: 'You may choose to use to determine if you qualify for additional allowances. Enter the value from line 17 of the worksheet, or you may also enter 0 allowances.',
      question_type: 'short_text',
      is_required: true,
      rules: [
        {
          category: 'regex',
          action: 'match',
          value: '^(0|[1-9][0-9]*)$',
        },
      ],
    },
  ],
};

export const additionalAmountInitialQuestion = [
  {
    id: 'additional_amount',
    title: 'Additional amount, if any, withheld from each pay period.',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^(0|[1-9][0-9]*)$',
      },
    ],
  },
];
